import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { forkJoin, of, Subscription, Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ApiModelStrategy, ApiSummaryChartOut, AvailableChartCurrenciesEnum, AvailableChartPeriodsEnum, ChartPointTyped, ClientCollaboratorOut, DetailInitData, DraftPortfolioStatus, DraftStatusResponse, ManagementTypesEnum, ModelPortfolioInstruments, PortfolioInstrumentSummaryOut, PortfolioOut, PriceMeasurements, SlideDirections, SummaryChartInput } from 'src/app/services/rest-api/rest-api';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { SelectItemTyped } from 'src/app/shared/dropdown/dropdown';
import { UserService } from 'src/app/services/user/user.service';
import { LoggedUser } from 'src/app/services/authentication/authentication';
import { DEFAULT_CURRENCY, InstrumentChartCardInput } from 'src/app/shared/charts/charts';
import { ModalService } from 'src/app/services/modal/modal.service';
import * as dayjs from 'dayjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { getDepositOrWithdrawValue } from 'src/app/shared/functions';
import { TranslateService } from '@ngx-translate/core';
import { hooverTypes } from 'src/app/shared/charts/custom-hover-tooltip';
import { CurrencyDigitShortcutPipeInput } from 'src/app/pipes/pipes';
import { TranslatedCurrencyYearInputs } from 'src/app/pipes/translated-currency-input-keys';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';
import { SignalsService } from 'src/app/services/signals.service';
import { RedirectService } from 'src/app/services/redirect.service';
import { ConsultantCommentaryModalInput } from 'src/app/services/modal/modal';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Location } from '@angular/common';
import { TranslatedConstantsService } from 'src/app/services/translated-constants.service';
import { charTypes } from 'src/app/shared/constants';
import { AcceptEfektikaNextAction, EfektikaClientService } from 'src/app/services/efektika-client.service';


@Component({
  templateUrl: './investment-detail.component.html',
  styleUrls: ['./investment-detail.component.scss'],
  standalone: false
})
export class InvestmentDetailComponent implements OnInit, OnDestroy {
  public preloaders = {
    initData: true,
    modelStrategies: true,
    selectedInvestment: true,
    chart: true,
    strategyPartialAccess: false
  };

  public errors = {
    chart: false,
  };

  public yearsShortcutsOptions: CurrencyDigitShortcutPipeInput = {
    units: TranslatedCurrencyYearInputs
  }

  public modelStrategies: ApiModelStrategy[] | null = null;

  public chartForm!: UntypedFormGroup;

  private get actualSelectedIndex(): number {
    return this.availableInvestments!.map(investment => investment.portfolioId)!.indexOf(this.selectedInvestment!.portfolioId);
  }

  public currencyFilter: SelectItemTyped<AvailableChartCurrenciesEnum>[] =
    this.translatedConstantsService.getTranslatedCurrencies()

  public filtrablePeriods: SelectItemTyped<AvailableChartPeriodsEnum>[] =
    this.translatedConstantsService.getTranslatedPeriods()

  public slideDirections = SlideDirections;

  public get loggedUser(): LoggedUser {
    if(!this.userService.loggedUser || this.userService.loggedUser === null) {
      this.router.navigate(['/login']);
    }

    return this.userService.loggedUser!;
  }

  public instrumentChart: InstrumentChartCardInput = {
      id: charTypes.INSTRUMENTS,
      chartData: [],
  };

  public chartValues: ChartPointTyped[] = [];
  public chartLabels: Date[] = [];
  public deposits: ChartPointTyped[] = [];
  public depositsLinePoints: ChartPointTyped[] = [];

  public availableInvestments: null | ExpectedOwnedStrategy[] = null;
  public selectedInvestment: PortfolioOut | null = null;
  public selectedInvestmentInstrumentsRatios: PortfolioInstrumentSummaryOut | null = null;
  public selectedInvestmentExtended!: ExpectedOwnedStrategy;

  private formSubscription?: Subscription;
  private dataRefetchSub?: Subscription;
  public isDraft = false;
  public draftCommentary: ConsultantCommentaryModalInput | null = null;
  private currentRouteIsDraft = false;
  private previousRouteIsDraft = false;
  private routeParamsSub?: Subscription;
  private routePramMapSub?: Subscription;
  private efektikaDraftAcceptModalChaningSub?: Subscription;

  public get positiveYield(): boolean {
    if(!this.selectedInvestmentExtended.actualYield) {
      return false;
    }
    return this.selectedInvestmentExtended.actualYield > 0;
  }

  public constructor(
    private route: ActivatedRoute,
    private restApiService: RestApiService,
    public toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private userService: UserService,
    public modalService: ModalService,
    private router: Router,
    private translate: TranslateService,
    private signalsService: SignalsService,
    private redirectService: RedirectService,
    private sanitizer: DomSanitizer,
    private location: Location,
    private translatedConstantsService: TranslatedConstantsService,
    private efektikaClientService: EfektikaClientService,
  ) { }

  public ngOnInit(): void {
    this.routePramMapSub = this.route.paramMap.pipe(take(1)).subscribe(params => {
      this.loadDetailData(params);
    });
    this.draftToPortfolioReloadDataHandler();
    this.handleEfektikaModalsChainingStream();
  }

  private handleEfektikaModalsChainingStream() {
    this.efektikaDraftAcceptModalChaningSub = this.efektikaClientService.acceptingEfektikaConfirmAction$
      .subscribe(({action, data: draftPortfolioId}) => {
        
        if (action === AcceptEfektikaNextAction.MODAL_DIP_CONFIRMATION) {
          this.modalService.openEfektikaDipModal();
        }

        if (action === AcceptEfektikaNextAction.MODAL_REGULAR_LIMIT_EXCEEDED_CONFIRMATION) {
          this.modalService.openEfektikaRegularExceededModal();
        }

        if(action === AcceptEfektikaNextAction.CREATE_REQUEST && draftPortfolioId) {
          this.restApiService.acceptDraftPortfolio(draftPortfolioId).subscribe({
            next: res => this.modalService.handleDraftAccepted(res),
            error: err => this.modalService.handleAcceptDraftError(err),
          });
        }

      });
  }

  private draftToPortfolioReloadDataHandler() {
    // To enable reload from draft -> portfolio/strategy
    // Previous route must be draft and current not be draft
    // This check is necessary to prevent reloading and resetting forms when navigating between investments
    this.routeParamsSub = this.route.paramMap.subscribe((params: ParamMap) => {
      this.currentRouteIsDraft = params?.get('isDraft') === '1';
      // at this point, isDraft has not been updated yet and this relates to previous route, updated for current route later in handleInitData
      this.previousRouteIsDraft = this.isDraft;

      if (!this.currentRouteIsDraft && this.previousRouteIsDraft) {
        this.loadDetailData(params)
        // reset helper props
        this.currentRouteIsDraft = false;
        this.previousRouteIsDraft = false;
      }
    });
  }

  private loadDetailData(params?: ParamMap): void {
    const neccessaryInitReqests: [
      Observable<ParamMap>,
      Observable<ExpectedOwnedStrategy[]>,
      Observable<ExpectedOwnedStrategy[]>,
    ] = [
      this.route.queryParamMap.pipe(take(1)),
      this.restApiService.getPortfoliosAndStrategies(),
      this.restApiService
        .getClientDraftPortfoliosAndStrategies()
        .pipe(catchError(() => of([]))),
    ];

    forkJoin(neccessaryInitReqests)
      .pipe(
        take(1),
        map(
          ([queryParams, portfolios, drafts]) =>
            ({
              params,
              queryParams,
              availableInvestments: portfolios,
              drafts,
            }) as DetailInitData,
        ),
      )
      .subscribe({
        error: this.handleInitError.bind(this),
        next: this.handleInitData.bind(this),
      });

    if(this.loggedUser.managementType === ManagementTypesEnum.EXECUTION_ONLY) {
      this.restApiService.getModelStrategiesWithInstrumentsCategoriesFilter().subscribe({
        error: this.handleModelStrategiesError.bind(this),
        next: this.handleModelStrategiesData.bind(this),
      });
    }
    this.initForm();
  }

  private initForm(): void {
    this.chartForm = new UntypedFormGroup({
      showDepositsOnChart: new UntypedFormControl(true),
      priceMeasure: new UntypedFormControl(this.currencyFilter[0].value),
      period: new UntypedFormControl(this.filtrablePeriods[0].value),
    })

    this.formSubscription = this.chartForm.valueChanges.pipe(distinctUntilChanged()).subscribe( () => this.updateChart() );
  }

  private handleModelStrategiesData(data: ApiModelStrategy[]): void {
    this.preloaders.modelStrategies = false;
    this.modelStrategies = data;
  }

  private handleModelStrategiesError(error: unknown): void {
    this.preloaders.modelStrategies = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  private handleInitError(error: unknown): void {
    this.preloaders.initData = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    this.toastService.unknownApiError();
  }

  public isTwrSelected(): boolean {
    return this.chartForm.value['priceMeasure'].value === '%';
  }

  private handleInitData(initData: DetailInitData): void {
    // split to prevent merging of ID and draft via parsed ';'/'%3B' character
    const parsedParams = initData.params?.get('investmentId')?.split(';');
    const paramsInvestmentId = parseInt(parsedParams?.[0] || '');
    this.isDraft =
      (initData.params?.get('isDraft') ??
      initData.queryParams.get('isDraft') ??
      (parsedParams?.[1]?.split('=')[1])) === '1';

    if(initData.availableInvestments.length === 0 && !this.isDraft) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.noAvailableInvestments',
        type: 'warning',
        component: ToastNotificationComponent
      });
      this.router.navigate(['/summary']);
      return;
    }

    if (!this.isDraft) {
      this.availableInvestments = initData.availableInvestments;
      const initInvestment = this.availableInvestments.find(investment => investment.portfolioId === +paramsInvestmentId) ?? this.availableInvestments[0];
      this.location.go('/investment-detail/' + initInvestment.portfolioId);
      this.selectedInvestmentExtended = initInvestment;

      this.loadPortfolioDetail(initInvestment.portfolioId);
    }

    if (this.isDraft && paramsInvestmentId) {
      const draftDetail = initData.drafts.find(draft => draft.draftPortfolioId === paramsInvestmentId);
      if (!draftDetail) {
        this.restApiService.getDraftPortfolioStatus(+paramsInvestmentId).subscribe({
          next: this.handleGetDraftStatusData.bind(this),
          error: this.handleGetDraftStatusError.bind(this),
        });
      }
      if (draftDetail) {
        this.selectedInvestmentExtended = draftDetail;
        this.preloaders.selectedInvestment = false;
        this.preloaders.initData = false;
        this.handleInvestmentInstrumentsForDraft(draftDetail);
        this.openDraftCommentaryModal();
      }
    } else if (this.isDraft && Number.isNaN(+paramsInvestmentId)) {
      // If there is an issue with the param, theat as draft forbidden
      this.handleGetDraftStatusError();
    }

    this.preloaders.initData = false;
  }

  private handleGetDraftStatusData(res: DraftStatusResponse) {
    const message: string = this.getNotMessageByDraftStatus(res.draftPortfolioStatus);
    this.toastService.addNotification({
      message: message,
      type: res.draftPortfolioStatus === DraftPortfolioStatus.ACCEPTED
              ? 'success'
              : 'warning',
      component: ToastNotificationComponent
    });
    if (res.draftPortfolioStatus === DraftPortfolioStatus.ACCEPTED && res.portfolioId) {
      this.router.navigate(['/investment-detail/' + res.portfolioId]);
    } else {
      this.router.navigate(['/summary']);
    }
  }

  private handleGetDraftStatusError() {
    this.toastService.addNotification({
      message: 'toastNotifications.messages.draftNotAccessed',
      type: 'warning',
      component: ToastNotificationComponent
    });
    this.router.navigate(['/summary']);
  }

  private getNotMessageByDraftStatus(status: DraftPortfolioStatus): string {
    switch(status) {
      case DraftPortfolioStatus.ACCEPTED: {
        return 'toastNotifications.messages.draftNotActiveAccepted';
      }
      case DraftPortfolioStatus.REFUSED: {
        return 'toastNotifications.messages.draftNotActiveRefused';
      }
      case DraftPortfolioStatus.EXPIRED: {
        return 'toastNotifications.messages.draftNotActiveExpired';
      }
      default: {
        return 'toastNotifications.messages.draftNotAccessed';
      }
    }
  }

  public openDraftCommentaryModal(): void {
    if (!this.isDraft || !this.selectedInvestmentExtended) {
      return;
    }
    if (
      this.selectedInvestmentExtended.collaborator &&
      this.selectedInvestmentExtended.consultantPhoto &&
      this.selectedInvestmentExtended.messageForClient
    ) {
      this.displayDraftCommentary(
        this.selectedInvestmentExtended.collaborator,
        this.selectedInvestmentExtended.consultantPhoto,
        this.selectedInvestmentExtended.messageForClient,
      );
    }
  }

    public displayDraftCommentary(consultData: ClientCollaboratorOut, photo: SafeUrl, commentary: string): void {
    const consultant = {
      name: (consultData.name.titleBefore ?? '') + ' ' + consultData.name.firstName ,
      surname: consultData.name.lastName + ' ' + (consultData.name.titleAfter ?? ''),
      phone: consultData.phone.prefix + consultData.phone.number,
      email: consultData.email,
    };
    this.draftCommentary = {
      consultant,
      commentary: this.sanitizer.bypassSecurityTrustHtml(commentary),
      photo
    };
  }

  private handleInvestmentDetailData(data: { investmentDetail: PortfolioOut, investmentRatios: PortfolioInstrumentSummaryOut }): void {
    data.investmentDetail = this.fixCashInstrumentCategory(data.investmentDetail);
    this.selectedInvestment = data.investmentDetail;
    this.selectedInvestmentInstrumentsRatios = data.investmentRatios;

    this.updateInstrumentChartsData(data.investmentDetail);
    this.updateChart();

    this.preloaders.selectedInvestment = false;
  }

  private handleInvestmentInstrumentsForDraft(data: ExpectedOwnedStrategy): void {
    if (!data.isDraft || !this.isDraft || !data.modelPortfolioInstruments?.length) {
      return;
    }

    data = {...data, modelPortfolioInstruments
    : data.modelPortfolioInstruments.map(mpi => {
      if (mpi.instrument.cash) {
        return ({...mpi, instrument: {...mpi.instrument, instrumentCategory: 'CASH'}});
      } else {
        return mpi;
      }
    })};

    this.updateInstrumentChartsDataForDraft(data.modelPortfolioInstruments);
    // this.updateChart(); // TODO Q: Right?

    // draft cant have any cash invested in, so just push 0
    this.selectedInvestmentInstrumentsRatios = {
      portfolioId: data.draftPortfolioId,
      totalAmount: 0,
      currencyCode: data.currency ?? 'CZK',
      instruments: [],
    };

    this.preloaders.selectedInvestment = false;
  }

  private fixCashInstrumentCategory(data: PortfolioOut): PortfolioOut {
    const cashIndex = data.modelPortfolioInstruments.findIndex(instrument => instrument.instrument.cash);
    data.modelPortfolioInstruments[cashIndex].instrument.instrumentCategory = 'CASH';
    return data;
  }

  private updateInstrumentChartsData(data: PortfolioOut): void {
    this.instrumentChart.chartData.length = 0;
    this.instrumentChart.chartData.push(...(data.modelPortfolioInstruments.map(instrument => {
      return {
        instrument: instrument.instrument,
        weight: instrument.weight,
      }
    })));
  }

  private updateInstrumentChartsDataForDraft(mpis: ModelPortfolioInstruments[] | undefined): void {
    if (!mpis) {
      return;
    }
    this.instrumentChart.chartData.length = 0;
    this.instrumentChart.chartData.push(...(mpis.map(instrument => {
      return {
        instrument: instrument.instrument,
        weight: instrument.weight,
      }
    })));
  }

  private handleInvestmentDetailError(error: unknown, showUnknownApiErrNot = true): void {
    this.preloaders.selectedInvestment = false;
    this.errors.chart = true;
    this.preloaders.chart = false;
    this.loggerService.error(typeof error === 'function' ? error() : error);
    if (showUnknownApiErrNot) {
      this.toastService.unknownApiError();
    }
  }

  public slideNextExist(): boolean {
    return !!this.availableInvestments![this.actualSelectedIndex + 1];
  }

  public slidePrevExist(): boolean {
    return !!this.availableInvestments![this.actualSelectedIndex - 1];
  }

  public slideSelected(direction: SlideDirections): void {
    let nextInvestment: ExpectedOwnedStrategy;
    this.preloaders.selectedInvestment = true;

    if (direction === SlideDirections.PREV) {
      nextInvestment = this.availableInvestments![this.actualSelectedIndex - 1];
    } else if (direction === SlideDirections.NEXT) {
      nextInvestment = this.availableInvestments![this.actualSelectedIndex + 1];
    } else {
      throw new Error('Unknown direction input!');
    }

    this.selectedInvestmentExtended = nextInvestment;

    this.router.navigate(['/investment-detail/' + nextInvestment.portfolioId], { replaceUrl: true });

    this.loadPortfolioDetail(nextInvestment.portfolioId);
  }

  private loadPortfolioDetail(portfolioId: number) {
    const initRequests = [
      this.restApiService.getInvestmentDetail({ portfolioId }),
      this.restApiService.getModelPortfolioInstruments({ portfolioId }),
    ];

    forkJoin(initRequests).pipe(
      take(1),
      map(
        ([ investmentDetail, investmentRatios ]) =>
        ({ investmentDetail, investmentRatios } as { investmentDetail: PortfolioOut, investmentRatios: PortfolioInstrumentSummaryOut })
      ),
    ).subscribe({
        error: this.handleInvestmentDetailError.bind(this),
        next: this.handleInvestmentDetailData.bind(this),
    });
  }

  private updateChart(): void {
    this.preloaders.chart = true;

    const params: SummaryChartInput = {
      priceMeasure: this.isTwrSelected() ? PriceMeasurements.TWR : PriceMeasurements.VALUE,
    };

    const period = this.chartForm.value['period'].value as AvailableChartPeriodsEnum;

    if(period !== AvailableChartPeriodsEnum.ALL) {
      const currentDate = dayjs();

      if(period === AvailableChartPeriodsEnum.Y1 || period === AvailableChartPeriodsEnum.Y3 || period === AvailableChartPeriodsEnum.Y5) {
        params['fromDate'] = currentDate.subtract(period, 'year');
      }
      if(period === AvailableChartPeriodsEnum.YTD) {
        const startOfYear = currentDate.startOf('year');
        params['fromDate'] = currentDate.isBefore(startOfYear) ? startOfYear.subtract(1, 'year') : startOfYear;
      }
    }

    if(this.isTwrSelected()) {
      if(this.chartForm.value['showDepositsOnChart']) {
        this.chartForm.patchValue({ showDepositsOnChart: false });
        return;
      }
    }

    this.restApiService.getInvestmentChart(params, this.selectedInvestment!.portfolioId + '').subscribe({
      error: this.handleSummaryChartError.bind(this),
      next: this.handleSummaryChartData.bind(this),
    });
  }

  private handleSummaryChartError(error: unknown): void {
    this.preloaders.chart = false;
    this.errors.chart = true;
    this.loggerService.error(typeof error === 'function' ? error() : error);

    this.toastService.unknownApiError();
  }

  private handleSummaryChartData(data: ApiSummaryChartOut): void {
    this.preloaders.chart = false;
    this.errors.chart = false;

    this.chartLabels.length = 0;
    this.chartLabels.push(...data.priceInTime.map(
      transaction => {
        return new Date(transaction.x * 1000) // unix time
    }));

    this.chartValues.length = 0;
    this.chartValues.push(...data.priceInTime.map(
      transaction => {
        return {
          x: transaction.x * 1000, // unix time
          y: this.isTwrSelected() ? transaction.y * 100 : transaction.y,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.chartValue,
        }
    }));

    this.depositsLinePoints.length = 0;
    this.depositsLinePoints.push(...data.depositsInTime.map(
      transaction => {
        return {
          x: transaction.x * 1000, // unix time
          y: this.isTwrSelected() ? transaction.y * 100 : transaction.y,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.depositsLinePoint,
        }
    }));

    this.deposits.length = 0;
    this.deposits.push(...data.moneyTransactionsInTime.map(
      transaction => {
        let depositY = transaction.sumOfDeposits;
        const correspondingDayValue = this.depositsLinePoints.find(day => day.x === transaction.x * 1000);

        if(correspondingDayValue) {
          depositY = correspondingDayValue.y;
        } else {
          const index = this.depositsLinePoints.findIndex(val => val.x >= (transaction.x * 1000));

          const closestCharValues = {
            down: this.depositsLinePoints[index - 1],
            up: this.depositsLinePoints[index],
          };

          try {
            const aproximateY = (closestCharValues.up.y + closestCharValues.down.y) / 2;
            depositY = aproximateY;
          } catch (error) {
            console.error('Catched chart error:', error);
          }
        }

        return {
          x: transaction.x * 1000,
          y: depositY,
          depositValue: getDepositOrWithdrawValue(transaction.x, data.moneyTransactionsInTime),
          type: hooverTypes.deposit,
        }
      }));
  }

  public deleteInvestment(): void {
    if(!this.availableInvestments) {
      throw new Error('Investments not initiated!');
    }
    const selectedInvestment = this.availableInvestments.find(investment => investment.portfolioId === this.selectedInvestment!.portfolioId);
    if(!selectedInvestment) {
      this.toastService.addNotification({
        message: 'toastNotifications.messages.strategyEditLoadError',
        type: 'warning',
        component: ToastNotificationComponent
      });
      return;
    }

    this.dataRefetchSub = this.signalsService.investmentDeleted$.subscribe(() => {
      this.preloaders.chart = true;
      this.preloaders.initData = true;
      this.preloaders.selectedInvestment = true;
      this.selectedInvestment = null;
      if(this.dataRefetchSub) {
        this.dataRefetchSub.unsubscribe();
      }
      this.loadDetailData();
    });

    this.modalService.openInvestmentDelete(selectedInvestment);
  }

  public openFeesModal(): void {
    this.modalService.openPortfolioFeesModal({ portfolioId: this.selectedInvestment!.portfolioId }, this.loggedUser.currencyCode ?? DEFAULT_CURRENCY);
  }

  public openMoneyModal(): void {
    this.modalService.openMoneyDepositModal(this.selectedInvestment!.portfolioId);
  }

  public openConsultModal(): void {
    this.modalService.openConsultModal();
  }

  public ngOnDestroy(): void {
    this.dataRefetchSub?.unsubscribe();
    this.formSubscription?.unsubscribe();
    this.routeParamsSub?.unsubscribe();
    this.routePramMapSub?.unsubscribe();
    this.efektikaDraftAcceptModalChaningSub?.unsubscribe();
  }

  public loadStrategyEditForPMWithStrategies (): void {
    if (this.loggedUser.managementType === 'PORTFOLIO_MANAGEMENT') {
      this.preloaders.strategyPartialAccess = true;
    }
  }

  public openDraftAcceptRefuseConfirm(portfolio: ExpectedOwnedStrategy, type: 'accept' | 'refuse'): void {
    if (portfolio.efektika && type === 'accept') {
      this.modalService.openEfektikaDraftAcceptConfirm(portfolio);
    } else {
      this.modalService.openDraftAcceptRefuseConfirm(portfolio, type, 'detail');
    }
  }

  public goToAMLForm(): void {
    this.redirectService.goToAmlFormExternalForm();
  }
}
